import { useState, useRef, useEffect } from 'react';
import { z } from 'zod';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import DashboardLayout from '@/components/layout/DashboardLayout';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectSeparator,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Upload as UploadIcon, FileImage, Loader2, CheckCircle2, Bookmark, BookmarkCheck, Copy, Check, Plus, X, ArrowRight, Sparkles } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { getCurrentTerm } from '@/lib/term';
import { formatDisplayName, type AnonymisationType } from '@/lib/pupils';
import { canCreateAssessment, canCreatePupil, getPlanLimits, planLimitMessage } from '@/lib/entitlements';
import { getUpgradeCTA } from '@/lib/upgrade';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  mapGrade,
  acceptAssessment,
  overrideAssessment,
  WRITING_TYPE_LABELS,
  FICTION_TYPES,
  NONFICTION_TYPES,
  type AssessmentGrade,
  type WritingType,
} from '@/lib/assessment';
import GradeBadge from '@/components/GradeBadge';
import { MarkdownText } from '@/components/MarkdownText';
import { Progress } from '@/components/ui/progress';
import { Badge } from '@/components/ui/badge';
import { trackEvent } from '@/lib/analytics';
import { getMimeType, mimeToExt } from '@/lib/writingFiles';
import type { FluencyScore } from '@/lib/fluency';
import { useWritingPages } from '@/hooks/useWritingPages';
import WritingPagesInput from '@/components/WritingPagesInput';
import { AssessmentTextBlock, FluencyScoreGrid, ProvisionalGradeNote } from '@/components/assessment/ResultParts';

const YEAR_GROUPS = [
  'Reception',
  'Year 1',
  'Year 2',
  'Year 3',
  'Year 4',
  'Year 5',
  'Year 6',
];

const formSchema = z.object({
  childName: z.string().min(1, 'Please select a student'),
  customName: z.string().optional(),
  yearGroup: z.string().min(1, 'Please select a year group'),
  writingType: z.string().min(1, 'Please select a writing type'),
}).refine(
  (data) => data.childName !== '__new__',
  { message: 'Add the new pupil before assessing', path: ['childName'] }
);

type FormValues = z.infer<typeof formSchema>;

interface AssessmentResult {
  grade: string;
  assessment: string;
  extracted_text?: string | null;
  fluency_score?: FluencyScore | null;
  possible_duplicate?: boolean;
}

interface ClassOption {
  id: string;
  name: string;
  year_groups: { name: string } | null;
}

interface Pupil {
  id: string;
  first_name: string;
  last_name: string;
  anonymisation: AnonymisationType;
  class_id: string | null;
  classes: {
    name: string;
    year_groups: { name: string } | null;
  } | null;
}

export default function Upload() {
  const { user, profile, hasRole, schoolPlan, planTier, planSubTier } = useAuth();
  // Individual plan users are school_admin but must use the teacher_classes path
  // so only their own class's pupils appear and the structured save path activates correctly
  const isAdmin = hasRole('school_admin') && schoolPlan === 'school';
  const isSchoolTeacherOnly = planTier === 'school' && !isAdmin;
  const { toast } = useToast();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const debugMode = searchParams.get('debugUpload') === '1';
  // Initialise from localStorage so remounts don't wipe the history.
  const [debugLog, setDebugLog] = useState<string[]>(() => {
    if (!debugMode) return [];
    try { return (localStorage.getItem('wa_dbg') || '').split('\n').filter(Boolean); }
    catch { return []; }
  });

  const [pupils, setPupils] = useState<Pupil[]>([]);
  const [pupilMap, setPupilMap] = useState<Map<string, Pupil>>(new Map());
  const [savedNames, setSavedNames] = useState<string[]>([]);
  // Inline "Add new pupil": every assessment is linked to a real pupil so it gets
  // Accept/Override, the image, Ask AI and report comments.
  const [classOptions, setClassOptions] = useState<ClassOption[]>([]);
  const [newFirstName, setNewFirstName] = useState('');
  const [newLastName, setNewLastName] = useState('');
  const [newClassId, setNewClassId] = useState('');
  const [isAddingPupil, setIsAddingPupil] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [result, setResult] = useState<AssessmentResult | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [isSaved, setIsSaved] = useState(false);
  const [isLoadingPupils, setIsLoadingPupils] = useState(true);
  const [limitReached, setLimitReached] = useState<'billing' | 'cap' | null>(null);

  // Structured assessment state
  const resultRef = useRef<HTMLDivElement>(null);

  const [structuredAssessmentId, setStructuredAssessmentId] = useState<string | null>(null);
  const [structuredAiGrade, setStructuredAiGrade] = useState<AssessmentGrade | null>(null);
  const [gradeAction, setGradeAction] = useState<'none' | 'accepting' | 'accepted' | 'overriding' | 'overridden'>('none');
  const [overrideGrade, setOverrideGrade] = useState<AssessmentGrade | null>(null);
  const [isActioning, setIsActioning] = useState(false);

  useEffect(() => {
    if (result && resultRef.current) {
      resultRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, [result]);

  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      childName: '',
      customName: '',
      yearGroup: '',
      writingType: '',
    },
  });

  const selectedChild = form.watch('childName');

  // Auto-fill year group from pupil's class → year_group when a registered pupil is selected
  useEffect(() => {
    if (!selectedChild || selectedChild === '__new__') return;
    const pupil = pupilMap.get(selectedChild);
    const yearGroupName = pupil?.classes?.year_groups?.name;
    if (yearGroupName) {
      form.setValue('yearGroup', yearGroupName, { shouldValidate: true });
    }
  }, [selectedChild, pupilMap, form]);

  const pupilFullNamesLower = new Set(pupils.map((p) => `${p.first_name} ${p.last_name}`.toLowerCase()));
  const extraSavedNames = savedNames.filter((n) => !pupilFullNamesLower.has(n.toLowerCase()));

  // Fetch pupils the teacher has access to
  useEffect(() => {
    if (!user) {
      setIsLoadingPupils(false);
      return;
    }
    const fetchPupils = async () => {
      if (isAdmin) {
        const { data, error } = await supabase
          .from('pupils')
          .select('id, first_name, last_name, anonymisation, class_id, classes(name, year_groups(name))')
          .eq('is_archived', false)
          .order('last_name');
        if (error) {
          toast({ title: 'Could not load pupils', description: error.message, variant: 'destructive' });
        } else if (data) {
          setPupils(data as unknown as Pupil[]);
          const map = new Map<string, Pupil>();
          for (const p of data) map.set(p.id, p as unknown as Pupil);
          setPupilMap(map);
        }
      } else {
        const { data: tcData, error: tcError } = await supabase
          .from('teacher_classes')
          .select('class_id')
          .eq('user_id', user!.id);
        if (tcError) {
          toast({ title: 'Could not load classes', description: tcError.message, variant: 'destructive' });
          return;
        }
        const classIds = (tcData ?? []).map((tc) => tc.class_id);
        if (classIds.length === 0) {
          setPupils([]);
          setPupilMap(new Map());
          return;
        }
        const { data, error } = await supabase
          .from('pupils')
          .select('id, first_name, last_name, anonymisation, class_id, classes(name, year_groups(name))')
          .eq('is_archived', false)
          .in('class_id', classIds)
          .order('last_name');
        if (error) {
          toast({ title: 'Could not load pupils', description: error.message, variant: 'destructive' });
        } else if (data) {
          setPupils(data as unknown as Pupil[]);
          const map = new Map<string, Pupil>();
          for (const p of data) map.set(p.id, p as unknown as Pupil);
          setPupilMap(map);
        }
      }
    };
    const fetchSavedNames = async () => {
      const { data, error } = await supabase
        .from('saved_assessments')
        .select('child_name')
        .eq('assessed_by', user.id);
      if (error) {
        toast({ title: 'Could not load previous assessments', description: error.message, variant: 'destructive' });
      } else if (data) {
        const unique = [...new Set(data.map((r) => r.child_name))].sort();
        setSavedNames(unique);
      }
    };
    const fetchClasses = async () => {
      let data: ClassOption[] | null = null;
      if (isAdmin) {
        if (!profile?.school_id) return;
        const res = await supabase
          .from('classes')
          .select('id, name, year_groups!inner(name, key_stages!inner(school_id))')
          .eq('year_groups.key_stages.school_id', profile.school_id)
          .order('name');
        data = res.data as unknown as ClassOption[] | null;
      } else {
        const { data: tcData } = await supabase
          .from('teacher_classes')
          .select('class_id')
          .eq('user_id', user.id);
        const classIds = (tcData ?? []).map((tc) => tc.class_id);
        if (classIds.length === 0) {
          setClassOptions([]);
          return;
        }
        const res = await supabase
          .from('classes')
          .select('id, name, year_groups(name)')
          .in('id', classIds)
          .order('name');
        data = res.data as unknown as ClassOption[] | null;
      }
      const options = data ?? [];
      setClassOptions(options);
      if (options.length === 1) setNewClassId(options[0].id);
    };
    Promise.all([fetchPupils(), fetchSavedNames(), fetchClasses()]).finally(() => {
      setIsLoadingPupils(false);
    });
  }, [user, isAdmin, profile?.school_id]);

  const handleAddPupil = async () => {
    const first = newFirstName.trim();
    const last = newLastName.trim();
    if (!first || !last) {
      toast({ title: 'Enter a first and last name', variant: 'destructive' });
      return;
    }
    if (!newClassId) {
      toast({ title: 'Choose a class for this pupil', variant: 'destructive' });
      return;
    }
    const existing = pupils.find(
      (p) => p.class_id === newClassId &&
        p.first_name.toLowerCase() === first.toLowerCase() &&
        p.last_name.toLowerCase() === last.toLowerCase(),
    );
    if (existing) {
      form.setValue('childName', existing.id, { shouldValidate: true });
      toast({ title: `${first} ${last} is already in this class`, description: 'They have been selected.' });
      return;
    }
    if (!canCreatePupil(planTier, pupils.length)) {
      const limits = getPlanLimits(planTier);
      toast({
        title: 'Pupil limit reached',
        description: planLimitMessage('pupil', limits.maxPupils, planTier),
        variant: 'destructive',
      });
      return;
    }
    setIsAddingPupil(true);
    const { data, error } = await supabase
      .from('pupils')
      .insert({ first_name: first, last_name: last, class_id: newClassId, anonymisation: 'full_name' })
      .select('id, first_name, last_name, anonymisation, class_id, classes(name, year_groups(name))')
      .single();
    setIsAddingPupil(false);
    if (error || !data) {
      toast({ title: 'Could not add pupil', description: error?.message, variant: 'destructive' });
      return;
    }
    const pupil = data as unknown as Pupil;
    setPupils((prev) => [...prev, pupil].sort((a, b) => a.last_name.localeCompare(b.last_name)));
    setPupilMap((prev) => new Map(prev).set(pupil.id, pupil));
    setNewFirstName('');
    setNewLastName('');
    form.setValue('childName', pupil.id, { shouldValidate: true });
    toast({ title: `${first} ${last} added to your class.` });
  };

  // Picking a name from the old typed-name history starts "Add new pupil" pre-filled,
  // so that pupil's next assessment gets the full feature set.
  const handleChildChange = (value: string, onChange: (v: string) => void) => {
    if (extraSavedNames.includes(value)) {
      const [first, ...rest] = value.trim().split(/\s+/);
      setNewFirstName(first ?? '');
      setNewLastName(rest.join(' '));
      onChange('__new__');
      return;
    }
    onChange(value);
  };

  // Write a debug line to both localStorage (survives remounts) and React state.
  const dbgWrite = (line: string) => {
    try {
      const prev = (localStorage.getItem('wa_dbg') || '').split('\n').filter(Boolean);
      prev.push(line);
      localStorage.setItem('wa_dbg', prev.slice(-50).join('\n'));
    } catch { /* private browsing */ }
    setDebugLog(prev => [...prev.slice(-49), line]);
  };

  const {
    pages,
    fileError,
    setFileError,
    openFilePicker,
    removePage: handleRemovePage,
    clearPersisted,
  } = useWritingPages({
    idbName: 'wa_upload',
    maxPages: 6,
    onFileError: (message) => toast({ title: 'Could not add file', description: message, variant: 'destructive' }),
    debug: {
      enabled: debugMode,
      log: (line) => setDebugLog(prev => [...prev.slice(-49), line]),
      write: dbgWrite,
      persist: (line) => {
        try {
          const prev = (localStorage.getItem('wa_dbg') || '').split('\n').filter(Boolean);
          prev.push(line);
          localStorage.setItem('wa_dbg', prev.slice(-50).join('\n'));
        } catch { /* private browsing */ }
      },
    },
  });

  // Count mounts so we can detect if the component remounts after picker close.
  useEffect(() => {
    if (!debugMode) return;
    const n = parseInt(localStorage.getItem('wa_mnt') || '0', 10) + 1;
    localStorage.setItem('wa_mnt', String(n));
    dbgWrite(`[mount #${n}] pages=${pages.length}`);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSave = async () => {
    if (!result || !user) return;
    setIsSaving(true);
    const rawChild = form.getValues('childName');
    const saveChildName = rawChild === '__custom__'
      ? form.getValues('customName')!.trim()
      : rawChild;
    const { error } = await supabase.from('saved_assessments').insert({
      assessed_by: user.id,
      child_name: saveChildName,
      year_group: form.getValues('yearGroup'),
      grade: result.grade,
      assessment_text: result.assessment,
      term: getCurrentTerm(),
    });
    setIsSaving(false);
    if (error) {
      toast({ title: 'Error', description: error.message, variant: 'destructive' });
    } else {
      setIsSaved(true);
      if (!savedNames.includes(saveChildName)) {
        setSavedNames((prev) => [...prev, saveChildName].sort());
      }
      toast({ title: 'Saved', description: 'Assessment saved successfully.' });
    }
  };

  const handleAccept = async () => {
    if (!structuredAssessmentId) return;
    setGradeAction('accepting');
    setIsActioning(true);
    const error = await acceptAssessment(structuredAssessmentId, structuredAiGrade);
    setIsActioning(false);
    if (error) {
      toast({ title: 'Error accepting grade', description: error.message, variant: 'destructive' });
      setGradeAction('none');
    } else {
      setGradeAction('accepted');
    }
  };

  const handleConfirmOverride = async () => {
    if (!structuredAssessmentId || !overrideGrade) return;
    setGradeAction('overriding');
    setIsActioning(true);
    const error = await overrideAssessment(structuredAssessmentId, overrideGrade);
    setIsActioning(false);
    if (error) {
      toast({ title: 'Error overriding grade', description: error.message, variant: 'destructive' });
      setGradeAction('overriding'); // keep in overriding state so user can retry
    } else {
      setGradeAction('overridden');
    }
  };

  const onSubmit = async (values: FormValues) => {
    if (pages.length === 0) {
      setFileError('Please upload at least one handwritten work image.');
      return;
    }
    if (!user) return;

    setLimitReached(null);

    // Client-side monthly assessment pre-flight (server enforces authoritatively).
    const limits = getPlanLimits(planTier);
    if (limits.maxAssessments !== -1) {
      const monthStart = new Date();
      monthStart.setUTCDate(1);
      monthStart.setUTCHours(0, 0, 0, 0);
      const { count } = await supabase
        .from('writing_samples')
        .select('id', { count: 'exact', head: true })
        .eq('uploaded_by', user.id)
        .gte('created_at', monthStart.toISOString());
      if (!canCreateAssessment(planTier, count ?? 0)) {
        setLimitReached('cap');
        toast({
          title: 'Assessment limit reached',
          description: planLimitMessage('assessment', limits.maxAssessments, planTier),
          variant: 'destructive',
        });
        return;
      }
    }

    setIsSubmitting(true);
    clearPersisted();
    setResult(null);
    // Reset structured assessment state
    setStructuredAssessmentId(null);
    setStructuredAiGrade(null);
    setGradeAction('none');
    setOverrideGrade(null);
    setIsActioning(false);

    try {
      // 1. Upload all pages sequentially — fail fast on first error
      const filePaths: string[] = [];
      const publicUrls: string[] = [];

      for (let i = 0; i < pages.length; i++) {
        const page = pages[i];
        const mime = getMimeType(page.file);
        const rawExt = page.file.name.includes('.') ? page.file.name.split('.').pop() : undefined;
        const ext = rawExt || mimeToExt(mime);
        const filePath = `${user.id}/${Date.now()}_${crypto.randomUUID()}.${ext}`;

        const { error: uploadError } = await supabase.storage
          .from('writing-samples')
          .upload(filePath, page.file, { contentType: mime || 'image/jpeg' });

        if (uploadError) throw new Error(`Upload failed (page ${i + 1}): ${uploadError.message}`);

        const { data: signedUrlData, error: signedUrlError } = await supabase.storage
          .from('writing-samples')
          .createSignedUrl(filePath, 3600);

        if (signedUrlError || !signedUrlData?.signedUrl) throw new Error(`Could not generate URL for page ${i + 1}`);

        filePaths.push(filePath);
        publicUrls.push(signedUrlData.signedUrl);
      }

      const childName = values.childName;
      const maybePupil = childName !== '__custom__' ? pupilMap.get(childName) : undefined;
      const isStructuredPupil =
        childName !== '__custom__' &&
        !extraSavedNames.includes(childName) &&
        maybePupil !== undefined;

      // Derive display name for the edge function
      const childDisplayName = isStructuredPupil && maybePupil
        ? formatDisplayName(maybePupil.first_name, maybePupil.last_name, maybePupil.anonymisation)
        : childName === '__custom__'
          ? values.customName!.trim()
          : childName;

      // 2. Call edge function with ordered imageUrls array
      const { data: fnData, error: fnError } = await supabase.functions.invoke(
        'assess-writing',
        {
          body: {
            childName: childDisplayName,
            customName: values.childName === '__custom__' ? values.customName!.trim() : '',
            yearGroup: values.yearGroup,
            imageUrls: publicUrls,
            pupilId: isStructuredPupil && maybePupil ? maybePupil.id : undefined,
          },
        }
      );

      if (fnError) {
        // FunctionsHttpError.message is always "Edge Function returned a non-2xx status code".
        // The actual error string is in the response body — read it from fnError.context.
        let msg = fnError.message ?? '';
        try {
          const body = await (fnError as any).context?.json?.();
          if (typeof body?.error === 'string') msg = body.error;
        } catch { /* body not JSON or already consumed */ }

        if (msg.startsWith('Unreadable:')) {
          toast({
            title: "We couldn't read the writing",
            description: msg.replace(/^Unreadable:\s*/, ''),
            variant: 'destructive',
          });
          return;
        }
        if (msg.startsWith('PaymentRequired:')) {
          setLimitReached('billing');
          toast({
            title: 'Subscription required',
            description: 'Your subscription is no longer active. Visit Settings to reactivate your plan.',
            variant: 'destructive',
          });
          return;
        }
        if (msg.startsWith('Forbidden:') || msg.includes('assessment limit')) {
          setLimitReached('cap');
          toast({
            title: 'Monthly limit reached',
            description: isSchoolTeacherOnly
              ? "Your school's monthly assessment limit has been reached. Please contact your school admin."
              : "You've reached your monthly assessment limit. Upgrade your plan to continue.",
            variant: 'destructive',
          });
          return;
        }
        throw new Error(msg);
      }

      const attemptId: string | null = fnData?.attempt_id ?? null;

      // 3. Structured path: persist to writing_samples + assessments
      if (isStructuredPupil) {
        const sampleTitle = `${childDisplayName} – ${values.yearGroup} – ${new Date().toISOString().slice(0, 10)}`;

        const { data: sampleData, error: sampleError } = await supabase
          .from('writing_samples')
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          .insert({
            pupil_id: childName,
            image_url: filePaths[0],   // first page (backward compat)
            image_urls: filePaths,     // all pages in order
            title: sampleTitle,
            writing_type: values.writingType as WritingType,
            uploaded_by: user.id,
            sample_date: new Date().toISOString().slice(0, 10),
          } as any)
          .select('id')
          .single();

        if (sampleError) {
          if (attemptId) {
            await supabase.rpc('mark_attempt_save_failed', {
              p_attempt_id: attemptId,
              p_message: sampleError.message,
              p_error_shown: 'Error saving writing sample',
            });
          }
          toast({ title: 'Error saving writing sample', description: sampleError.message, variant: 'destructive' });
          throw new Error(sampleError.message);
        }

        const { data: assessmentData, error: assessmentError } = await supabase
          .from('assessments')
          .insert({
            writing_sample_id: sampleData.id,
            assessed_by: user.id,
            ai_grade: mapGrade(fnData.grade),
            justification: fnData.assessment,
            extracted_text: fnData.extracted_text ?? null,
            fluency_score: fnData.fluency_score ?? null,
            possible_duplicate: fnData?.possible_duplicate ?? null,
            status: 'pending',
          })
          .select('id')
          .single();

        if (assessmentError) {
          if (attemptId) {
            await supabase.rpc('mark_attempt_save_failed', {
              p_attempt_id: attemptId,
              p_message: assessmentError.message,
              p_error_shown: 'Error saving assessment',
            });
          }
          toast({ title: 'Error saving assessment', description: assessmentError.message, variant: 'destructive' });
          throw new Error(assessmentError.message);
        }

        setStructuredAssessmentId(assessmentData.id);
        setStructuredAiGrade(mapGrade(fnData.grade));
      }

      setIsSaved(false);
      setResult({
        grade: fnData?.grade ?? 'N/A',
        assessment: fnData?.assessment ?? 'No assessment returned.',
        extracted_text: fnData?.extracted_text ?? null,
        fluency_score: fnData?.fluency_score ?? null,
        possible_duplicate: fnData?.possible_duplicate ?? false,
      });

      // GA4 key event: a teacher got an assessment back. No pupil data is sent.
      trackEvent('assessment_completed', {
        grade: fnData?.grade ?? 'unknown',
        possible_duplicate: fnData?.possible_duplicate ?? false,
      });

      toast({
        title: 'Assessment complete',
        description: fnData?.possible_duplicate
          ? 'Results are displayed below. This looks like a piece already assessed for this pupil — check before accepting.'
          : 'Results are displayed below.',
      });
    } catch (err: unknown) {
      if (err instanceof Error) {
        toast({ title: 'Error', description: err.message, variant: 'destructive' });
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <DashboardLayout>
      <div className="p-6 max-w-2xl mx-auto space-y-6">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <UploadIcon className="h-5 w-5 text-primary" />
              Student Writing Assessment
            </CardTitle>
            <CardDescription>
              Upload student handwritten work for assessment.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Form {...form}>
              <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-5">
                {/* Child's Name */}
                <FormField
                  control={form.control}
                  name="childName"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Child's Name</FormLabel>
                      <Select onValueChange={(v) => handleChildChange(v, field.onChange)} value={field.value}>
                        <FormControl>
                          <SelectTrigger disabled={isLoadingPupils}>
                            {isLoadingPupils ? (
                              <span className="flex items-center gap-2 text-muted-foreground">
                                <Loader2 className="h-4 w-4 animate-spin" />
                                Loading pupils...
                              </span>
                            ) : (
                              <SelectValue placeholder="Select a student..." />
                            )}
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          {pupils.length > 0 && (
                            <SelectGroup>
                              <SelectLabel>Class Pupils</SelectLabel>
                              {pupils.map((p) => (
                                <SelectItem key={p.id} value={p.id}>
                                  {formatDisplayName(p.first_name, p.last_name, p.anonymisation)}
                                </SelectItem>
                              ))}
                            </SelectGroup>
                          )}
                          {extraSavedNames.length > 0 && schoolPlan === 'school' && (
                            <SelectGroup>
                              <SelectLabel>Previously Assessed</SelectLabel>
                              {extraSavedNames.map((name) => (
                                <SelectItem key={name} value={name}>
                                  {name}
                                </SelectItem>
                              ))}
                            </SelectGroup>
                          )}
                          <SelectSeparator />
                          <SelectItem value="__new__">
                            + Add new pupil
                          </SelectItem>
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                {/* Inline add-pupil */}
                {selectedChild === '__new__' && (
                  <div className="space-y-3 rounded-md border p-3">
                    {classOptions.length === 0 ? (
                      <p className="text-sm text-muted-foreground">
                        You need a class before adding pupils. Create one in <strong>My Classes</strong>, or ask your school admin to assign you to a class.
                      </p>
                    ) : (
                      <>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                          <div className="space-y-1.5">
                            <label htmlFor="new-pupil-first" className="text-sm font-medium">First name</label>
                            <Input id="new-pupil-first" value={newFirstName} onChange={(e) => setNewFirstName(e.target.value)} />
                          </div>
                          <div className="space-y-1.5">
                            <label htmlFor="new-pupil-last" className="text-sm font-medium">Last name</label>
                            <Input id="new-pupil-last" value={newLastName} onChange={(e) => setNewLastName(e.target.value)} />
                          </div>
                        </div>
                        <div className="space-y-1.5">
                          <label className="text-sm font-medium">Class</label>
                          <Select value={newClassId} onValueChange={setNewClassId}>
                            <SelectTrigger>
                              <SelectValue placeholder="Choose a class..." />
                            </SelectTrigger>
                            <SelectContent>
                              {classOptions.map((c) => (
                                <SelectItem key={c.id} value={c.id}>
                                  {c.name}{c.year_groups?.name ? ` (${c.year_groups.name})` : ''}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                        <Button type="button" onClick={handleAddPupil} disabled={isAddingPupil} className="w-full sm:w-auto">
                          {isAddingPupil ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Plus className="h-4 w-4 mr-2" />}
                          Add pupil
                        </Button>
                      </>
                    )}
                  </div>
                )}

                {/* Class info (read-only, shown when a registered pupil has a class assigned) */}
                {(() => {
                  if (!selectedChild || selectedChild === '__new__') return null;
                  if (extraSavedNames.includes(selectedChild)) return null;
                  const pupil = pupilMap.get(selectedChild);
                  const className = pupil?.classes?.name;
                  if (!className) return null;
                  return (
                    <div className="space-y-2">
                      <label className="text-sm font-medium leading-none">Class</label>
                      <div className="flex h-9 w-full items-center rounded-md border border-input bg-muted px-3 py-2 text-sm text-muted-foreground">
                        {className}
                      </div>
                    </div>
                  );
                })()}

                {/* Year Group */}
                <FormField
                  control={form.control}
                  name="yearGroup"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Year Group</FormLabel>
                      <Select onValueChange={field.onChange} value={field.value}>
                        <FormControl>
                          <SelectTrigger>
                            <SelectValue placeholder="Select year group..." />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          {YEAR_GROUPS.map((yg) => (
                            <SelectItem key={yg} value={yg}>
                              {yg}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                {/* Writing Type */}
                <FormField
                  control={form.control}
                  name="writingType"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Writing Type</FormLabel>
                      <Select onValueChange={field.onChange} value={field.value}>
                        <FormControl>
                          <SelectTrigger>
                            <SelectValue placeholder="Select writing type..." />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          <SelectGroup>
                            <SelectLabel>Fiction</SelectLabel>
                            {FICTION_TYPES.map((wt) => (
                              <SelectItem key={wt} value={wt}>
                                {WRITING_TYPE_LABELS[wt]}
                              </SelectItem>
                            ))}
                          </SelectGroup>
                          <SelectSeparator />
                          <SelectGroup>
                            <SelectLabel>Non-fiction</SelectLabel>
                            {NONFICTION_TYPES.map((wt) => (
                              <SelectItem key={wt} value={wt}>
                                {WRITING_TYPE_LABELS[wt]}
                              </SelectItem>
                            ))}
                          </SelectGroup>
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                {/* File Upload — multi-page (1–6 images) */}
                <WritingPagesInput
                  label="Handwritten Work Images"
                  hint="Take a photo, choose from camera roll, or upload a PDF · max 6 pages · 20 MB each"
                  pages={pages}
                  maxPages={6}
                  error={fileError}
                  onAdd={openFilePicker}
                  onRemove={handleRemovePage}
                />

                <Button type="submit" className="w-full" disabled={isSubmitting}>
                  {isSubmitting ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Assessing...
                    </>
                  ) : (
                    'Assess Writing'
                  )}
                </Button>
              </form>
            </Form>
          </CardContent>
        </Card>

        {/* In-page limit / billing CTA */}
        {limitReached && (
          <Card className={`border-${limitReached === 'billing' ? 'destructive/30 bg-destructive/5' : 'amber-200 bg-amber-50'}`}>
            <CardContent className="pt-5 space-y-3">
              {isSchoolTeacherOnly ? (
                <div className="space-y-1">
                  <p className="text-sm font-medium">
                    {limitReached === 'billing' ? 'Subscription required' : 'Monthly limit reached'}
                  </p>
                  <p className="text-sm text-muted-foreground">
                    {limitReached === 'billing'
                      ? "Your school’s subscription is no longer active. Please contact your school admin."
                      : "Your school’s monthly assessment limit has been reached. Please contact your school admin to discuss upgrading."}
                  </p>
                </div>
              ) : (() => {
                const cta = getUpgradeCTA(planTier, planSubTier ?? null, schoolPlan);
                return cta ? (
                  <div className="space-y-3">
                    <div className="space-y-1">
                      <p className="text-sm font-medium flex items-center gap-1.5">
                        <Sparkles className="h-4 w-4 text-primary" />
                        {limitReached === 'billing' ? 'Subscription required' : cta.heading}
                      </p>
                      <p className="text-sm text-muted-foreground">
                        {limitReached === 'billing'
                          ? 'Your subscription is no longer active. Reactivate or upgrade your plan to continue.'
                          : cta.description}
                      </p>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      <Button size="sm" onClick={() => navigate(limitReached === 'billing' ? (planTier === 'school' ? '/school-billing' : '/individual-billing') : cta.primaryRoute)}>
                        {limitReached === 'billing' ? 'Reactivate Plan' : cta.primaryLabel}
                        <ArrowRight className="ml-1.5 h-3.5 w-3.5" />
                      </Button>
                      {limitReached !== 'billing' && cta.secondaryLabel && cta.secondaryRoute && (
                        <Button size="sm" variant="outline" onClick={() => navigate(cta.secondaryRoute!)}>
                          {cta.secondaryLabel}
                          <ArrowRight className="ml-1.5 h-3.5 w-3.5" />
                        </Button>
                      )}
                    </div>
                  </div>
                ) : null;
              })()}
            </CardContent>
          </Card>
        )}

        {/* Results */}
        {result && (
          <div ref={resultRef}>
          <Card className="border-primary/30 bg-primary/5">
            <CardHeader>
              <CardTitle className="flex items-center justify-between gap-2 text-lg">
                <span className="flex items-center gap-2">
                  <CheckCircle2 className="h-5 w-5 text-primary" />
                  Assessment Result
                </span>
                {result.possible_duplicate && (
                  <Badge variant="outline" className="text-warning border-warning">
                    Possible Duplicate
                  </Badge>
                )}
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <span className="text-sm font-medium text-muted-foreground">Suggested grade</span>
                <div className="mt-1">
                  {structuredAssessmentId && structuredAiGrade ? (
                    <GradeBadge grade={structuredAiGrade} />
                  ) : (
                    <p className="text-xl font-bold">{result.grade}</p>
                  )}
                </div>
                <ProvisionalGradeNote />
              </div>
              <AssessmentTextBlock text={result.assessment} />

              {/* Fluency scores */}
              {result.fluency_score && <FluencyScoreGrid score={result.fluency_score} />}

              {/* Structured path: Accept / Override */}
              {structuredAssessmentId ? (
                <div className="space-y-3 pt-1">
                  {gradeAction === 'none' && (
                    <div className="flex gap-2">
                      <Button
                        onClick={handleAccept}
                        disabled={isActioning}
                        variant="default"
                        className="flex-1"
                      >
                        Accept Grade
                      </Button>
                      <Button
                        onClick={() => setGradeAction('overriding')}
                        disabled={isActioning}
                        variant="outline"
                        className="flex-1 text-warning border-warning"
                      >
                        Override Grade
                      </Button>
                    </div>
                  )}

                  {gradeAction === 'accepting' && (
                    <div className="flex gap-2">
                      <Button disabled variant="default" className="flex-1">
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        Accepting...
                      </Button>
                      <Button disabled variant="outline" className="flex-1 text-warning border-warning">
                        Override Grade
                      </Button>
                    </div>
                  )}

                  {gradeAction === 'overriding' && (
                    <div className="space-y-2">
                      <Select
                        value={overrideGrade ?? ''}
                        onValueChange={(v) => setOverrideGrade(v as AssessmentGrade)}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Select grade..." />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="working_towards">Working Towards</SelectItem>
                          <SelectItem value="expected">Expected</SelectItem>
                          <SelectItem value="greater_depth">Greater Depth</SelectItem>
                        </SelectContent>
                      </Select>
                      <div className="flex gap-2">
                        <Button
                          onClick={handleConfirmOverride}
                          disabled={isActioning || !overrideGrade}
                          variant="destructive"
                          className="flex-1"
                        >
                          {isActioning ? (
                            <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Confirming...</>
                          ) : (
                            'Confirm Override'
                          )}
                        </Button>
                        <Button
                          onClick={() => { setGradeAction('none'); setOverrideGrade(null); }}
                          disabled={isActioning}
                          variant="ghost"
                          className="flex-1"
                        >
                          Cancel
                        </Button>
                      </div>
                    </div>
                  )}

                  {(gradeAction === 'accepted' || gradeAction === 'overridden') && (
                    <div className="flex items-center gap-2 text-success text-sm font-medium pt-1">
                      <CheckCircle2 className="h-4 w-4" />
                      Grade recorded.
                      {gradeAction === 'accepted' && structuredAiGrade && (
                        <GradeBadge grade={structuredAiGrade} size="sm" />
                      )}
                      {gradeAction === 'overridden' && overrideGrade && (
                        <GradeBadge grade={overrideGrade} size="sm" />
                      )}
                    </div>
                  )}
                </div>
              ) : (
                /* Custom / saved-name path: existing Save button */
                <Button
                  onClick={handleSave}
                  disabled={isSaving || isSaved}
                  variant="outline"
                  className="w-full mt-2"
                >
                  {isSaved ? (
                    <><BookmarkCheck className="mr-2 h-4 w-4" /> Saved</>
                  ) : isSaving ? (
                    <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Saving...</>
                  ) : (
                    <><Bookmark className="mr-2 h-4 w-4" /> Save Assessment</>
                  )}
                </Button>
              )}
            </CardContent>
          </Card>
          </div>
        )}
        {/* Debug panel — only visible at ?debugUpload=1 */}
        {debugMode && (
          <Card className="border-amber-300 bg-amber-50">
            <CardHeader className="pb-2">
              <div className="flex items-center justify-between">
                <CardTitle className="text-xs font-mono text-amber-800">
                  debug · build {import.meta.env.VITE_VERCEL_GIT_COMMIT_SHA?.slice(0, 7) ?? 'local'}
                  {' '}· mount #{(() => { try { return localStorage.getItem('wa_mnt') ?? '?'; } catch { return '?'; } })()}
                  {' '}· pages={pages.length}
                </CardTitle>
                <button
                  type="button"
                  className="text-xs text-amber-700 underline ml-2"
                  onClick={() => {
                    try { localStorage.removeItem('wa_dbg'); localStorage.removeItem('wa_mnt'); } catch {}
                    setDebugLog([]);
                  }}
                >
                  Clear
                </button>
              </div>
            </CardHeader>
            <CardContent>
              {debugLog.length === 0
                ? <p className="text-xs text-amber-700">No log entries yet — tap Upload writing then select a file.</p>
                : <pre className="text-[10px] leading-4 font-mono whitespace-pre-wrap break-all text-amber-900 bg-amber-100 rounded p-2 max-h-64 overflow-y-auto">
                    {debugLog.join('\n')}
                  </pre>
              }
            </CardContent>
          </Card>
        )}
      </div>
    </DashboardLayout>
  );
}
